import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "@emotion/styled";

import Popover from "components/ui/popover/Popover";
import Holder from "lib/icons/Holder";
import { capitalize } from "../../../lib/utils";
import { Flex } from "../../../styles/utility-components";
import { TextLink } from "../../actions/Links";
import Button from "../../ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown_menu";
import { Stage } from "../../ui/form_components/StageSelect/Stage";
import Input from "../../ui/input/Input";
import { getGroupIcon, NewStage, STAGE_COLORS, StageGroup, WorkflowTransition } from "./utils";

type WorkflowStageProps = {
  transition: WorkflowTransition;
  generateSearchUrl: () => string;
  workflowType: "bid" | "lead";
  onStartEdit: () => void;
  onDeleteStage: () => void;
};

export function WorkflowStageRow({
  transition,
  generateSearchUrl,
  workflowType,
  onStartEdit,
  onDeleteStage,
}: WorkflowStageProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition: cssTransition,
  } = useSortable({ id: transition.id });

  return (
    <StageRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: cssTransition,
      }}
    >
      <Flex gap={8} alignItems="center">
        <DragHandle
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <Holder />
        </DragHandle>
        <Stage
          label={transition.stage.name}
          color={transition.stage.color}
          iconColor={transition.stage.iconColor}
          iconName={transition.stage.iconName}
        />
        <StyledLink to={generateSearchUrl()} isActive={transition.stage.count > 0}>
          {transition.stage.count} {`${capitalize(workflowType)}s`}
        </StyledLink>
      </Flex>
      <DropdownWrapper>
        <DropdownMenu
          align="end"
          width="160px"
          trigger={
            <DropdownMenuTrigger
              buttonType="tertiary"
              icon="dotsVertical"
              iconProps={{ color: "sysTextDefault" }}
              hideArrow
              aria-label={`Stage actions for ${transition.stage.name}`}
            />
          }
        >
          <DropdownMenuItem key="edit" icon="edit02" onClick={onStartEdit} aria-label="edit">
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem key="delete" aria-label="Delete" icon="trash03" onClick={onDeleteStage}>
            Delete
          </DropdownMenuItem>
        </DropdownMenu>
      </DropdownWrapper>
    </StageRow>
  );
}

const StageRow = styled(Flex)<{ isEditing?: boolean }>(({ theme, isEditing }) => ({
  padding: "12px 8px",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 8,
  gap: 8,
  ...(!isEditing && {
    "&:hover": {
      backgroundColor: theme.colors.sysBackgroundAlternative,
    },
  }),
}));

const DragHandle = styled(Flex)({
  opacity: 0,
  pointerEvents: "none",
  cursor: "grab",
  alignItems: "center",
  lineHeight: 0,
  flexShrink: 0,

  [`${StageRow}:hover &, &:focus-visible`]: {
    opacity: 1,
    pointerEvents: "auto",
  },

  "&:active": {
    cursor: "grabbing",
  },
});

const DropdownWrapper = styled.div({
  opacity: 0,
  visibility: "hidden",
  transition: "opacity 0.2s, visibility 0.2s",

  [`${StageRow}:hover &, &:focus-within`]: {
    opacity: 1,
    visibility: "visible",
  },
});

const StyledLink = styled(TextLink, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  color: isActive ? theme.colors.sysTextSecondary : theme.colors.sysTextInactive,
  fontSize: "12px",
  "&:hover": {
    color: theme.colors.sysTextSecondary,
  },
}));

type StageInputRowProps = {
  group: StageGroup;
  submitLabel: string;
  stage: NewStage;
  setStage: (stage: NewStage) => void;
  onSubmit: () => void;
  onCancel: () => void;
};

export function StageInputRow({
  group,
  submitLabel,
  onSubmit,
  onCancel,
  stage,
  setStage,
}: StageInputRowProps): JSX.Element {
  return (
    <StageRow isEditing>
      <Popover
        side="bottom"
        align="start"
        contentPadding={8}
        trigger={
          <DropdownMenuTrigger
            aria-label="Choose stage colour"
            icon={getGroupIcon(group)}
            iconProps={{
              color: stage.iconColor,
            }}
          />
        }
      >
        <Flex gap={4}>
          {STAGE_COLORS.map((color) => (
            <ColorOption
              key={color.stageBackgroundColor}
              style={{ backgroundColor: color.stageIconColor }}
              selected={color.stageIconColor === stage.iconColor}
              onClick={() =>
                setStage({
                  ...stage,
                  iconColor: color.stageIconColor,
                  backgroundColor: color.stageBackgroundColor,
                })
              }
              type="button"
              aria-label={`Select colour ${color.stageIconColor}`}
            />
          ))}
        </Flex>
      </Popover>
      <Input
        id={group.name}
        name={group.name}
        type="text"
        placeholder="Enter stage name"
        value={stage.name}
        onChange={(e) => setStage({ ...stage, name: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter") onSubmit();
        }}
        autoFocus
      />
      <Button buttonStyle="inverse" onClick={onCancel}>
        Cancel
      </Button>
      <Button onClick={onSubmit}>{submitLabel}</Button>
    </StageRow>
  );
}

const ColorOption = styled.button<{ selected: boolean }>(({ selected }) => ({
  width: 24,
  height: 24,
  borderRadius: 6,
  border: selected ? "2px solid rgba(0,0,0,0.4)" : "2px solid transparent",
  cursor: "pointer",
  padding: 0,
}));
